import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import toast from "react-hot-toast";

const STATUS_OPTIONS = ["new", "in_progress", "completed", "cancelled"];
const PRIORITY_OPTIONS = ["low", "medium", "high"];

// HH:MM:SS → HH:MM  (for <input type="time">)
const toTimeInput = (val) => (val ? val.substring(0, 5) : "");
// HH:MM → HH:MM:SS  (for API)
const toApiTime = (val) => (val ? `${val}:00` : null);

const SectionLabel = ({ children }) => (
  <Box sx={{ mt: 2, mb: 0.5 }}>
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: 1 }}
    >
      {children}
    </Typography>
    <Divider />
  </Box>
);

const JobEditDialog = ({ open, data, staffList = [], onClose, onUpdated }) => {
  const { company_id } = useSelector((state) => state.GlobalVariables);

  const initialForm = {
    job_name: "",
    job_category_id: "",
    staff_id: "",
    customer_id: "",
    site_id: "",
    job_date: "",
    planned_start_time: "",
    planned_end_time: "",
    status: "",
    priority: "",
    sla_minutes: "",
    remark: "",
  };

  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  // ── Pre-fill form when dialog opens with a row ────────────────────────────
  useEffect(() => {
    if (!open || !data) return;

    setForm({
      job_name:           data.job_name            || "",
      job_category_id:    data.job_category_id     || "",
      staff_id:           data.staff_id            || "",
      customer_id:        data.customer_id         || "",
      site_id:            data.site_id             || "",
      job_date:           data.job_date            || "",
      planned_start_time: toTimeInput(data.planned_start_time),
      planned_end_time:   toTimeInput(data.planned_end_time),
      status:             data.status              || "",
      priority:           data.priority            || "",
      sla_minutes:        data.sla_minutes != null ? String(data.sla_minutes) : "",
      remark:             "",
    });
  }, [open, data]);

  // ── Load dropdowns when dialog opens ─────────────────────────────────────
  useEffect(() => {
    if (!open || !company_id) return;

    const fetchDropdowns = async () => {
      setDropdownLoading(true);
      try {
        const [catRes, custRes] = await Promise.all([
          configServ.getActiveJobCategories({ company_id }),
          configServ.getSatandardActiveCustomer({ company_id }),
        ]);
        if (catRes?.status)  setCategoryList(catRes.data  || []);
        if (custRes?.status) setCustomerList(custRes.data || []);
      } catch (e) {
        console.error("Dropdown fetch error:", e);
      } finally {
        setDropdownLoading(false);
      }
    };

    fetchDropdowns();
  }, [open, company_id]);

  // ── Reload sites when customer changes ───────────────────────────────────
  useEffect(() => {
    if (!form.customer_id) {
      setSiteList([]);
      return;
    }
    const fetchSites = async () => {
      try {
        const res = await configServ.getShipToAddress({ cust_id: form.customer_id });
        setSiteList(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error("Site fetch error:", e);
        setSiteList([]);
      }
    };
    fetchSites();
  }, [form.customer_id]);

  // ── Field change handler ──────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      // Reset site when customer changes
      ...(name === "customer_id" ? { site_id: "" } : {}),
    }));
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build a partial payload — only include non-empty values
      const payload = {};

      if (form.job_name)        payload.job_name        = form.job_name;
      if (form.job_category_id) payload.job_category_id = Number(form.job_category_id);
      if (form.staff_id)        payload.staff_id        = Number(form.staff_id);
      if (form.customer_id)     payload.customer_id     = Number(form.customer_id);
      if (form.site_id)         payload.site_id         = Number(form.site_id);
      if (form.job_date)        payload.job_date        = form.job_date;
      if (form.planned_start_time) payload.planned_start_time = toApiTime(form.planned_start_time);
      if (form.planned_end_time)   payload.planned_end_time   = toApiTime(form.planned_end_time);
      if (form.status)          payload.status          = form.status;
      if (form.priority)        payload.priority        = form.priority;
      if (form.sla_minutes !== "") payload.sla_minutes  = Number(form.sla_minutes);
      if (form.remark.trim())   payload.remark          = form.remark.trim();

      const res = await configServ.updateJobDetail(data.id, payload);

      if (res?.status) {
        toast.success("Job updated successfully");
        onUpdated();
        onClose();
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{ bgcolor: "#1976d2", color: "#fff", py: 1.5 }}
      >
        <Typography variant="h6" fontSize="1rem">
          Edit Job Detail — ID #{data?.id}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {dropdownLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>

            {/* ── Job Info ──────────────────────────────── */}
            <Grid item xs={12}>
              <SectionLabel>Job Info</SectionLabel>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Job Name"
                name="job_name"
                value={form.job_name}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  name="job_category_id"
                  value={form.job_category_id}
                  label="Category"
                  onChange={handleChange}
                >
                  {categoryList.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.job_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ── Assignment ───────────────────────────── */}
            <Grid item xs={12}>
              <SectionLabel>Assignment</SectionLabel>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Staff</InputLabel>
                <Select
                  name="staff_id"
                  value={form.staff_id}
                  label="Staff"
                  onChange={handleChange}
                >
                  {staffList.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.contact_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Customer</InputLabel>
                <Select
                  name="customer_id"
                  value={form.customer_id}
                  label="Customer"
                  onChange={handleChange}
                >
                  {customerList.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.contact_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Site</InputLabel>
                <Select
                  name="site_id"
                  value={form.site_id}
                  label="Site"
                  onChange={handleChange}
                  disabled={!form.customer_id}
                >
                  {siteList.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.address}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ── Schedule ─────────────────────────────── */}
            <Grid item xs={12}>
              <SectionLabel>Schedule</SectionLabel>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                label="Job Date"
                name="job_date"
                value={form.job_date}
                onChange={handleChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                type="time"
                label="Planned Start"
                name="planned_start_time"
                value={form.planned_start_time}
                onChange={handleChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                type="time"
                label="Planned End"
                name="planned_end_time"
                value={form.planned_end_time}
                onChange={handleChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  step: 60,
                  min: form.planned_start_time || undefined,
                }}
              />
            </Grid>

            {/* ── Status & Priority ─────────────────────── */}
            <Grid item xs={12}>
              <SectionLabel>Status & Priority</SectionLabel>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={form.status}
                  label="Status"
                  onChange={handleChange}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                      {s.replace("_", " ")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={form.priority}
                  label="Priority"
                  onChange={handleChange}
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <MenuItem key={p} value={p} sx={{ textTransform: "capitalize" }}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                label="SLA (minutes)"
                name="sla_minutes"
                value={form.sla_minutes}
                onChange={handleChange}
                fullWidth
                size="small"
                inputProps={{ min: 1 }}
              />
            </Grid>

            {/* ── Remark ───────────────────────────────── */}
            <Grid item xs={12}>
              <SectionLabel>Supervisor Remark</SectionLabel>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Remark"
                name="remark"
                value={form.remark}
                onChange={handleChange}
                fullWidth
                size="small"
                multiline
                rows={2}
                inputProps={{ maxLength: 500 }}
                helperText="Optional — logged to tracking. Leave blank for no tracking entry."
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} /> : <SaveIcon />}
        >
          {loading ? "Saving…" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobEditDialog;
